import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, Lock, RotateCw } from 'lucide-react';
import ENTITY_ICONS from '../components/common/entityIcons';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import dashboardService from '../services/dashboard';
import Analytics from '../components/Analytics';
import Reports from '../components/Reports';

const FOCUS_RING = 'outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

const plural = (n, one, many = `${one}s`) => `${Number(n).toLocaleString()} ${n === 1 ? one : many}`;
/** "/users" + { app_id, status: '0' } -> "/users?app_id=…&status=0" (filter keys = the page's URL filter keys). */
const listUrl = (path, params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')).toString();
    return qs ? `${path}?${qs}` : path;
};

// --- Needs attention -----------------------------------------------------------------------

/**
 * Display copy for each risk signal the API returns (controllers/dashboard.js ATTENTION_KEYS).
 * `label(n)` names what was counted; `hint` says what to do; `path` is the list page where the
 * fix happens and `filters` its preset URL filters (shown there as removable chips), chosen
 * so the list shows exactly the rows counted here; `none` is the phrase used in the all-clear line.
 */
const SIGNALS = {
    locked_accounts: {
        icon: Lock,
        label: (n) => `${plural(n, 'account')} locked after failed sign-ins`,
        hint: 'Unlock from the Users page once you have confirmed the attempts were genuine.',
        path: '/users',
        filters: { status: '1', locked: '1' },
        page: 'Users',
        none: 'locked accounts',
    },
    api_keys_expired: {
        icon: ENTITY_ICONS.apiKeys,
        label: (n) => `${plural(n, 'API key')} expired but still active`,
        hint: 'Rotate or delete them; integrations using them are already failing.',
        path: '/api-keys',
        filters: { status: '1', expiry: 'expired' },
        page: 'API keys',
        none: 'expired API keys',
    },
    webhooks_auto_disabled: {
        icon: ENTITY_ICONS.webhooks,
        label: (n) => `${plural(n, 'webhook')} turned off after 10 failed deliveries`,
        hint: 'Fix the endpoint, then activate it again. Events are not being delivered.',
        path: '/webhooks',
        filters: { status: '0', failures_min: '10' },
        page: 'Webhooks',
        none: 'inactive webhooks',
    },
    webhooks_failing: {
        icon: ENTITY_ICONS.webhooks,
        label: (n) => `${plural(n, 'webhook')} failing deliveries`,
        hint: 'Check the delivery log. A webhook turns off after 10 consecutive failures.',
        path: '/webhooks',
        filters: { status: '1', failures_min: '1' },
        page: 'Webhooks',
        none: 'failing webhooks',
    },
    invitations_expiring: {
        icon: ENTITY_ICONS.invitations,
        label: (n) => `${plural(n, 'invitation')} expire within 48 hours`,
        hint: 'Remind the invitee or resend; an expired invitation cannot be accepted.',
        path: '/invitations',
        filters: { expiry: 'expiring' },
        page: 'Invitations',
        none: 'invitations about to expire',
    },
    api_keys_expiring: {
        icon: ENTITY_ICONS.apiKeys,
        label: (n) => `${plural(n, 'API key')} expire within 14 days`,
        hint: 'Rotate them before they expire to avoid an integration outage.',
        path: '/api-keys',
        filters: { status: '1', expiry: 'expiring' },
        page: 'API keys',
        none: 'API keys about to expire',
    },
};

const listPhrase = (items) =>
    items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;

const AttentionRow = ({ item }) => {
    const signal = SIGNALS[item.key];
    const Icon = signal.icon;
    return (
        <li className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                    <p className="font-medium text-slate-900">{signal.label(item.count)}</p>
                    <p className="mt-0.5 text-sm text-slate-600">{signal.hint}</p>
                </div>
            </div>
            <ul className="flex flex-wrap gap-2 pl-11 sm:shrink-0 sm:justify-end sm:pl-0" aria-label="By application">
                {item.apps.length === 0 ? (
                    <li>
                        <Link
                            to={listUrl(signal.path, signal.filters)}
                            className={`inline-flex rounded-md border border-slate-200 px-2.5 py-1 text-sm font-medium text-slate-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 ${FOCUS_RING}`}
                        >
                            Open {signal.page}
                        </Link>
                    </li>
                ) : (
                    item.apps.map((app) => (
                        <li key={app.app_id} className="min-w-0">
                            <Link
                                to={listUrl(signal.path, { app_id: app.app_id, ...signal.filters })}
                                title={`Open ${signal.page} for ${app.app_name}`}
                                className={`inline-flex max-w-[16rem] items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1 text-sm text-slate-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 ${FOCUS_RING}`}
                            >
                                <span className="truncate font-medium">{app.app_name}</span>
                                <span className="tabular-nums text-slate-600">{app.count}</span>
                            </Link>
                        </li>
                    ))
                )}
            </ul>
        </li>
    );
};

const NeedsAttention = ({ items }) => {
    const known = items.filter((i) => SIGNALS[i.key]);
    const open = known.filter((i) => i.count > 0);
    const clear = known.filter((i) => i.count === 0).map((i) => SIGNALS[i.key].none);

    return (
        <section aria-labelledby="attention-heading">
            <h2 id="attention-heading" className="mb-3 text-lg font-semibold text-slate-900">
                Needs attention
            </h2>
            <Card>
                <CardContent className="p-4 sm:p-5">
                    {open.length > 0 && (
                        <ul className="divide-y divide-slate-200">
                            {open.map((item) => (
                                <AttentionRow key={item.key} item={item} />
                            ))}
                        </ul>
                    )}
                    {clear.length > 0 && (
                        <p
                            className={`flex items-start gap-2 text-sm text-slate-600 ${
                                open.length > 0 ? 'mt-4 border-t border-slate-200 pt-4' : ''
                            }`}
                        >
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                            <span>
                                {open.length > 0 ? 'Otherwise clear: no ' : 'All clear: no '}
                                {listPhrase(clear)}.
                            </span>
                        </p>
                    )}
                </CardContent>
            </Card>
        </section>
    );
};

// --- Inventory strip ---------------------------------------------------------------------

/**
 * Each item's number equals what its drill target lists. Cross-app numbers open a per-app
 * breakdown (whose rows link to that app's page and add up to the headline) instead of
 * linking to one app's page, which would show a different number.
 */
const buildInventoryItems = (inv) => {
    const byApp = inv.by_app || [];
    const appsWith = (key) => byApp.filter((a) => a[key] > 0);
    const spread = (key) => {
        const n = appsWith(key).length;
        return n > 0 ? `across ${plural(n, 'app')}` : null;
    };
    const breakdown = (key, disabledKey, path) =>
        appsWith(key).map((a) => ({
            app_id: a.app_id,
            app_name: a.app_name,
            count: a[key],
            detail: disabledKey && a[disabledKey] > 0 ? `${a[disabledKey]} inactive` : null,
            detailTo: disabledKey && a[disabledKey] > 0 ? listUrl(path, { app_id: a.app_id, status: '0' }) : null,
            to: listUrl(path, { app_id: a.app_id }),
        }));
    // Cross-app counts open a per-app breakdown; when only one app has any, link straight to it.
    const direct = (rows) => (rows.length === 1 ? rows[0].to : undefined);
    const rolesRows = breakdown('roles', 'roles_disabled', '/roles');
    const componentsRows = breakdown('components', 'components_disabled', '/components');
    const tenantsRows = breakdown('tenants', 'tenants_disabled', '/tenants');

    return [
        {
            id: 'apps',
            label: inv.apps.total === 1 ? 'Application' : 'Applications',
            value: inv.apps.total,
            note: inv.apps.disabled > 0 ? `${inv.apps.disabled} inactive` : null,
            noteTo: inv.apps.disabled > 0 ? listUrl('/apps', { status: '0' }) : null,
            to: '/apps',
        },
        {
            id: 'accounts',
            label: inv.accounts.total === 1 ? 'User account' : 'User accounts',
            value: inv.accounts.total,
            note: inv.accounts.disabled > 0 ? `${inv.accounts.disabled} inactive` : 'with or without app access',
            noteTo: inv.accounts.disabled > 0 ? listUrl('/users', { status: '0' }) : null,
            noteTitle: 'Inactive users in the selected app',
            to: '/users',
            title: 'Every sign-in account on the platform, with or without app access. Opens the Users page for the selected application.',
        },
        {
            id: 'access',
            label: 'With app access',
            value: inv.users_with_access,
            note: 'active, in at least 1 app',
            breakdownTitle: 'Rows on each app’s Users page',
            breakdownNote:
                'Users pages also list inactive and removed members, and show a person once per tenant, so these rows don’t add up to the distinct total.',
            rows: byApp
                .filter((a) => a.member_rows > 0)
                .map((a) => {
                    const extra = a.member_rows - a.member_users;
                    return {
                        app_id: a.app_id,
                        app_name: a.app_name,
                        count: a.member_rows,
                        detail:
                            extra > 0
                                ? `${plural(a.member_users, 'person', 'people')}; ${
                                      extra === 1 ? '1 is' : `${extra} are`
                                  } in more than one tenant`
                                : null,
                        to: listUrl('/users', { app_id: a.app_id }),
                    };
                }),
        },
        {
            id: 'roles',
            label: inv.roles.total === 1 ? 'Role' : 'Roles',
            value: inv.roles.total,
            note: [spread('roles'), inv.roles.disabled > 0 ? `${inv.roles.disabled} disabled` : null]
                .filter(Boolean)
                .join(' · '),
            breakdownTitle: 'Roles per app',
            rows: rolesRows,
            to: direct(rolesRows),
            noteTo: rolesRows.length === 1 ? rolesRows[0].detailTo : null,
        },
        {
            id: 'components',
            label: inv.components.total === 1 ? 'Component' : 'Components',
            value: inv.components.total,
            note: [spread('components'), inv.components.disabled > 0 ? `${inv.components.disabled} disabled` : null]
                .filter(Boolean)
                .join(' · '),
            breakdownTitle: 'Components per app',
            rows: componentsRows,
            to: direct(componentsRows),
            noteTo: componentsRows.length === 1 ? componentsRows[0].detailTo : null,
        },
        {
            id: 'tenants',
            label: inv.tenants.total === 1 ? 'Tenant' : 'Tenants',
            value: inv.tenants.total,
            note: [spread('tenants'), inv.tenants.disabled > 0 ? `${inv.tenants.disabled} disabled` : null]
                .filter(Boolean)
                .join(' · '),
            breakdownTitle: 'Tenants per app',
            rows: tenantsRows,
            to: direct(tenantsRows),
            noteTo: tenantsRows.length === 1 ? tenantsRows[0].detailTo : null,
        },
    ];
};

const ItemBody = ({ item, expandable, expanded }) => (
    <>
        <span className="flex items-center gap-1 text-sm text-slate-600">
            {item.label}
            {expandable && (
                <ChevronDown
                    className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                />
            )}
        </span>
        <span className="block text-2xl font-semibold tabular-nums text-slate-900">
            {Number(item.value).toLocaleString()}
        </span>
        {item.note && !item.noteTo && <span className="block truncate text-xs text-slate-600">{item.note}</span>}
    </>
);

/** A "2 inactive" style note that opens the list filtered to those rows. */
const NoteLink = ({ item }) => (
    <Link
        to={item.noteTo}
        title={item.noteTitle}
        className={`relative z-10 inline-block max-w-full truncate rounded text-xs font-medium text-primary-700 underline-offset-2 hover:underline ${FOCUS_RING}`}
    >
        {item.note}
    </Link>
);

const Inventory = ({ inventory }) => {
    const [openId, setOpenId] = useState(null);
    const items = buildInventoryItems(inventory);
    const openItem = items.find((i) => i.id === openId);
    const cell = `block h-full w-full rounded-lg px-3 py-2.5 text-left ${FOCUS_RING}`;

    return (
        <section aria-labelledby="inventory-heading">
            <h2 id="inventory-heading" className="mb-3 text-lg font-semibold text-slate-900">
                Inventory
            </h2>
            <Card>
                <CardContent className="p-2">
                    <ul className="grid grid-cols-2 gap-1 sm:grid-cols-3 xl:grid-cols-6">
                        {items.map((item) => {
                            const expandable = Array.isArray(item.rows) && item.rows.length > 0;
                            if (item.to) {
                                // The note link sits beside (not inside) the item link: no nested anchors.
                                return (
                                    <li
                                        key={item.id}
                                        className="min-w-0 rounded-lg hover:bg-slate-50"
                                        title={item.title}
                                    >
                                        <Link to={item.to} className={`${cell} ${item.noteTo ? 'pb-0' : ''}`}>
                                            <ItemBody item={item} />
                                        </Link>
                                        {item.noteTo && (
                                            <div className="px-3 pb-2.5">
                                                <NoteLink item={item} />
                                            </div>
                                        )}
                                    </li>
                                );
                            }
                            if (expandable) {
                                const expanded = openId === item.id;
                                return (
                                    <li key={item.id} className="min-w-0">
                                        <button
                                            type="button"
                                            aria-expanded={expanded}
                                            aria-controls="inventory-breakdown"
                                            onClick={() => setOpenId(expanded ? null : item.id)}
                                            className={`${cell} ${expanded ? 'bg-primary-50' : 'hover:bg-slate-50'}`}
                                        >
                                            <ItemBody item={item} expandable expanded={expanded} />
                                        </button>
                                        {item.noteTo && (
                                            <div className="px-3 pb-2.5">
                                                <NoteLink item={item} />
                                            </div>
                                        )}
                                    </li>
                                );
                            }
                            return (
                                <li key={item.id} className="min-w-0" title={item.title}>
                                    <div className="px-3 py-2.5">
                                        <ItemBody item={item} />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    {openItem && (
                        <div id="inventory-breakdown" className="mx-1 mb-1 mt-2 border-t border-slate-200 px-2 pt-3">
                            <h3 className="text-sm font-medium text-slate-900">{openItem.breakdownTitle}</h3>
                            {openItem.breakdownNote && (
                                <p className="mt-1 text-xs text-slate-600">{openItem.breakdownNote}</p>
                            )}
                            <ul className="mt-2 divide-y divide-slate-100">
                                {openItem.rows.map((row) => (
                                    <li
                                        key={row.app_id}
                                        className="flex items-center justify-between gap-3 rounded-md px-2 py-2 text-sm hover:bg-slate-50"
                                    >
                                        <span className="min-w-0">
                                            <Link
                                                to={row.to}
                                                className={`block truncate rounded font-medium text-slate-900 hover:text-primary-700 ${FOCUS_RING}`}
                                            >
                                                {row.app_name}
                                            </Link>
                                            {row.detail &&
                                                (row.detailTo ? (
                                                    <Link
                                                        to={row.detailTo}
                                                        className={`rounded text-xs font-medium text-primary-700 underline-offset-2 hover:underline ${FOCUS_RING}`}
                                                    >
                                                        {row.detail}
                                                    </Link>
                                                ) : (
                                                    <span className="block text-xs text-slate-600">{row.detail}</span>
                                                ))}
                                        </span>
                                        <Link
                                            to={row.to}
                                            aria-label={`${row.count.toLocaleString()} in ${row.app_name}`}
                                            className={`shrink-0 rounded tabular-nums text-slate-900 hover:text-primary-700 ${FOCUS_RING}`}
                                        >
                                            {row.count.toLocaleString()}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </CardContent>
            </Card>
        </section>
    );
};

// --- Page --------------------------------------------------------------------------------

const LoadingState = () => (
    <div className="space-y-6" aria-busy="true" aria-label="Loading dashboard">
        <div className="h-40 animate-pulse rounded-xl bg-slate-200" />
        <div className="h-24 animate-pulse rounded-xl bg-slate-200" />
    </div>
);

const ErrorState = ({ onRetry, retrying }) => (
    <Card role="alert">
        <CardContent className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
                <div>
                    <p className="font-medium text-slate-900">Couldn’t load the dashboard</p>
                    <p className="text-sm text-slate-600">
                        The overview request failed, so no counts are shown. Check your connection and try again.
                    </p>
                </div>
            </div>
            <Button variant="outline" onClick={onRetry} disabled={retrying}>
                <RotateCw className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
                {retrying ? 'Retrying…' : 'Retry'}
            </Button>
        </CardContent>
    </Card>
);

const Overview = () => {
    const [state, setState] = useState({ status: 'loading', data: null });

    const load = useCallback(async () => {
        setState((s) => ({ ...s, status: s.status === 'error' ? 'retrying' : 'loading' }));
        try {
            const data = await dashboardService.getDashboardOverview();
            if (!data || !Array.isArray(data.attention) || !data.inventory) {
                throw new Error('Unexpected dashboard response');
            }
            setState({ status: 'ready', data });
        } catch (err) {
            console.error('Error loading dashboard data:', err);
            setState({ status: 'error', data: null });
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (state.status === 'loading') return <LoadingState />;
    if (state.status === 'error' || state.status === 'retrying') {
        return <ErrorState onRetry={load} retrying={state.status === 'retrying'} />;
    }

    return (
        <div className="space-y-6">
            <NeedsAttention items={state.data.attention} />
            <Inventory inventory={state.data.inventory} />
        </div>
    );
};

const DashboardPage = () => (
    <div className="min-w-0 space-y-6">
        <div>
            <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
            <p className="mt-1 text-slate-600">What needs action across every application, then what exists.</p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full max-w-xs grid-cols-2">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="insights">Insights</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
                <Overview />
            </TabsContent>

            <TabsContent value="insights" className="space-y-6">
                <Analytics />
                <Reports />
            </TabsContent>
        </Tabs>
    </div>
);

export default DashboardPage;
